import { ref, nextTick } from 'vue';

export function useAccessibleDropdown(options = {}) {
  const { onClose = () => {}, scrollBehavior = 'smooth', typeAheadTimeout = 500, wrapNavigation = true } = options;

  const focusedIndex = ref(-1);
  const activeDescendantId = ref('');
  const typeAheadString = ref('');
  const typeAheadTimeoutRef = ref(null);

  function setFocusedIndex(index, optionsLength, optionIdPrefix = 'option') {
    if (index >= 0 && index < optionsLength) {
      focusedIndex.value = index;
      activeDescendantId.value = `${optionIdPrefix}-${index}`;
      scrollOptionIntoView(index, optionIdPrefix);
      return true;
    }
    return false;
  }

  function clearFocusedIndex() {
    focusedIndex.value = -1;
    activeDescendantId.value = '';
  }

  function scrollOptionIntoView(index, optionIdPrefix = 'option') {
    nextTick(() => {
      const option = document.getElementById(`${optionIdPrefix}-${index}`);
      const container = option?.closest('[role="listbox"]') || option?.closest('.options-list');

      if (!option || !container) return;

      option.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: scrollBehavior });
    });
  }

  function navigateToOption(direction, optionsLength, optionIdPrefix = 'option') {
    let targetIndex;

    switch (direction) {
      case 'next':
        targetIndex =
          focusedIndex.value < optionsLength - 1 ? focusedIndex.value + 1 : wrapNavigation ? 0 : focusedIndex.value;
        break;
      case 'previous':
        targetIndex =
          focusedIndex.value > 0 ? focusedIndex.value - 1 : wrapNavigation ? optionsLength - 1 : focusedIndex.value;
        break;
      case 'first':
        targetIndex = 0;
        break;
      case 'last':
        targetIndex = optionsLength - 1;
        break;
      default:
        return false;
    }

    return setFocusedIndex(targetIndex, optionsLength, optionIdPrefix);
  }

  function initializeFocus(optionsList, optionIdPrefix = 'option', selectedGetter = (option) => option.checked) {
    if (optionsList.length === 0) return false;

    const firstSelectedIndex = optionsList.findIndex(selectedGetter);
    const targetIndex = firstSelectedIndex >= 0 ? firstSelectedIndex : 0;

    return setFocusedIndex(targetIndex, optionsList.length, optionIdPrefix);
  }

  function handleTypeAhead(character, optionsList, optionNameGetter = (option) => option.name) {
    if (typeAheadTimeoutRef.value) clearTimeout(typeAheadTimeoutRef.value);

    typeAheadString.value += character.toLowerCase();

    const findMatch = (startIndex = 0) =>
      optionsList.findIndex(
        (option, index) =>
          index >= startIndex && optionNameGetter(option).toLowerCase().startsWith(typeAheadString.value),
      );

    const matchIndex = findMatch(focusedIndex.value + 1);
    const finalIndex = matchIndex >= 0 ? matchIndex : findMatch(0);

    if (finalIndex >= 0) setFocusedIndex(finalIndex, optionsList.length);

    typeAheadTimeoutRef.value = setTimeout(() => {
      typeAheadString.value = '';
    }, typeAheadTimeout);
  }

  function handleTriggerKeydown(event, isOpen, onToggle) {
    const keyActions = {
      ArrowDown: () => onToggle(),
      ArrowUp: () => onToggle(),
      Enter: () => onToggle(),
      ' ': () => onToggle(),
      Escape: () => {
        if (isOpen) {
          onClose();
        }
        return { skipPreventDefault: true };
      },
      Tab: () => {
        if (isOpen) {
          onClose();
        }
        return { skipPreventDefault: true };
      },
    };

    const action = keyActions[event.key];
    if (action) {
      const { skipPreventDefault = false } = action() || {};
      if (!skipPreventDefault) {
        event.preventDefault();
      }
      return true;
    }
    return false;
  }

  function handleListboxKeydown(event, optionsList, optionIdPrefix = 'option', config = {}) {
    const {
      onSelect = () => {},
      onToggleSelection = () => {},
      returnFocusSelector = '.dropdown-trigger',
      optionNameGetter = (option) => option.name,
      allowMultiSelect = config.allowMultiSelect ?? true,
    } = config;
    const keyActions = {
      ArrowDown: () => navigateToOption('next', optionsList.length, optionIdPrefix),
      ArrowUp: () => navigateToOption('previous', optionsList.length, optionIdPrefix),
      Home: () => navigateToOption('first', optionsList.length, optionIdPrefix),
      End: () => navigateToOption('last', optionsList.length, optionIdPrefix),
      Enter: () => executeSelection(),
      ' ': () => executeSelection(),
      Escape: () => {
        onClose();
        nextTick(() => document.querySelector(returnFocusSelector)?.focus());
        return { skipStopPropagation: false };
      },
      Tab: () => {
        return { skipPreventDefault: true };
      },
    };

    function executeSelection() {
      if (focusedIndex.value >= 0) {
        const handler = allowMultiSelect ? onToggleSelection : onSelect;
        handler(focusedIndex.value);
      }
    }

    const action = keyActions[event.key];
    if (action) {
      const { skipPreventDefault = false, skipStopPropagation = true } = action() || {};

      if (!skipPreventDefault) {
        event.preventDefault();
      }
      // prevent closing the entire search modal if Escape is pressed when dropdown is open. Only dropdown should be closed
      if (!skipStopPropagation) {
        event.stopPropagation();
      }
      return true;
    }

    if (event.key.length === 1 && /[a-zA-Z0-9]/.test(event.key)) {
      handleTypeAhead(event.key, optionsList, optionNameGetter);
      return true;
    }

    return false;
  }

  function handleListboxFocus(optionsList, optionIdPrefix = 'option', selectedGetter = (option) => option.checked) {
    if (focusedIndex.value === -1) {
      initializeFocus(optionsList, optionIdPrefix, selectedGetter);
    }
  }

  function handleListboxBlur(event, containerRef, onCloseCallback, allowedSelectors = []) {
    const { relatedTarget } = event;

    if (
      relatedTarget &&
      allowedSelectors.some(
        (selector) => relatedTarget.matches?.(selector) || relatedTarget.classList?.contains(selector.replace('.', '')),
      )
    ) {
      return false;
    }

    nextTick(() => {
      if (containerRef.value && !containerRef.value.contains(document.activeElement)) {
        onCloseCallback();
      }
    });

    return true;
  }

  function cleanup() {
    if (typeAheadTimeoutRef.value) {
      clearTimeout(typeAheadTimeoutRef.value);
      typeAheadTimeoutRef.value = null;
    }
    clearFocusedIndex();
    typeAheadString.value = '';
  }

  return {
    focusedIndex,
    activeDescendantId,
    typeAheadString,
    setFocusedIndex,
    clearFocusedIndex,
    initializeFocus,
    navigateToOption,
    handleTriggerKeydown,
    handleListboxKeydown,
    handleListboxFocus,
    handleListboxBlur,
    cleanup,
    scrollOptionIntoView,
  };
}
